import { type StaticEventConfig } from './types'

/**
 * Checks if a specific date falls within a dynamic window around an anchor date.
 * Useful for events that shift every year (like CNY or Easter).
 * all dates are in UTC
 *
 * @param checkDate - The date we want to verify (usually 'today')
 * @param anchorDate - The main event date (e.g., CNY Day)
 * @param daysBefore - How many days before the anchor date are included (exclusive start?)
 * @param daysAfter - How many days after the anchor date are included
 */
export function isDateInDynamicWindow(
  checkDate: Date,
  anchorDate: Date,
  daysBefore: number,
  daysAfter: number,
): boolean {
  // Calculate Start Date
  const startDate = new Date(anchorDate)
  startDate.setUTCDate(anchorDate.getUTCDate() - daysBefore)

  // Calculate End Date
  const endDate = new Date(anchorDate)
  endDate.setUTCDate(anchorDate.getUTCDate() + daysAfter)
  // Add 23 hours, 59 minutes, 59 seconds, 999 milliseconds
  endDate.setUTCHours(
    endDate.getUTCHours() + 23,
    endDate.getUTCMinutes() + 59,
    endDate.getUTCSeconds() + 59,
    endDate.getUTCMilliseconds() + 999,
  )

  return checkDate >= startDate && checkDate <= endDate
}

/**
 * Checks if a specific date falls within a Month/Day range.
 * Handles year crossovers (e.g., Dec 25 to Jan 5).
 */
export function isDateInStaticRange(
  date: Date,
  config: Omit<StaticEventConfig, 'type' | 'kind' | 'priority'>,
): boolean {
  const month = date.getMonth() + 1 // JS months are 0-indexed
  const day = date.getDate()

  const { startMonth, startDay, endMonth, endDay } = config

  // Scenario 1: Range crosses year boundary (e.g., Dec to Jan)
  if (startMonth > endMonth) {
    const isAfterStart =
      month > startMonth || (month === startMonth && day >= startDay)
    const isBeforeEnd =
      month < endMonth || (month === endMonth && day <= endDay)
    return isAfterStart || isBeforeEnd
  }

  // Scenario 2: Standard range within same year
  const isAfterStart =
    month > startMonth || (month === startMonth && day >= startDay)
  const isBeforeEnd = month < endMonth || (month === endMonth && day <= endDay)

  return isAfterStart && isBeforeEnd
}

/**
 * Creates a Date object set to midnight in a specific timezone offset.
 * @param dateStr - Format: 'YYYY-MM-DD'
 * @param offset - Format: '+HH:mm' or '-HH:mm' (Defaults to current timezone)
 */
export function createDateWithOffset(
  dateStr: string,
  offset: string = '',
): Date {
  // We ensure the date string is treated as local 'wall clock' time
  // at that specific offset to avoid "day-shifting" bugs.
  const isoString = `${dateStr}T00:00:00${offset}`
  const date = new Date(isoString)

  // Validation: Check if the string actually resulted in a valid date
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid Date or Offset provided: ${isoString}`)
  }

  return date
}
